import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ListFooter } from '../ui';
import { BillHoldsBanner } from './Finance';
import { nearMatches } from '../utils/nearMatch';

// Payment requests — raise (ad-hoc / release), approve (finance_approver/management),
// and track my own. Ad-hoc payee is ALWAYS a pick from pdc_payees (no free text).
// Gates are backend-enforced; the UI never offers a raiser their own request to approve.

const APPROVER_ROLES = ['finance_approver', 'management', 'admin'];
// RAISE is broad; CURATING the payee master is narrow. Only these roles may ADD a
// payee (mirrors the backend POST /finance/pdc/payees gate). Everyone else picks
// from the existing master — no free text — so it doesn't fill with name drift.
const PAYEE_ADMIN_ROLES = ['admin', 'management', 'accounts'];
const ST = {
  draft: { bg: '#F7F6F3', c: '#6a6a6a', label: 'Draft' },
  pending: { bg: '#FEF3E2', c: '#B45309', label: 'Pending' },
  approved: { bg: '#EAF7EE', c: '#166534', label: 'Approved' },
  rejected: { bg: '#FCEBEB', c: '#A32D2D', label: 'Rejected' },
  cancelled: { bg: '#eee', c: '#888', label: 'Cancelled' },
  paid: { bg: '#E7F0FB', c: '#1D4E89', label: 'Paid' },
};
const PAY_MODES = ['cash', 'bank', 'cheque', 'upi', 'neft', 'rtgs'];
// Only accounts/admin (the executor) records payment — mirrors the backend PAY gate.
const PAY_ROLES = ['accounts', 'admin'];
const inr = (v) => '₹' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
// db.js returns a SQL DATE as a plain 'YYYY-MM-DD' string, so this reorders text and never
// constructs a Date — the timezone shift that fix exists to prevent stays prevented.
const fmtD = (d) => { const p = String(d || '').slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : (d || ''); };
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase' };
const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5' };
const badge = (s) => { const t = ST[s] || ST.draft; return <span style={{ padding: '2px 9px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: t.bg, color: t.c }}>{t.label}</span>; };
const THRESHOLD = 20000;
// A purchase bill booked at receipt is captured review_status='pending' and stays unreleasable until
// Finance certifies it (POST /finance/purchase-invoices/:id/certify, CERT = admin / management /
// accounts / finance_approver). The raiser usually cannot do that, so the screen says who can.
const CERTIFIER_HINT = 'Accounts, a finance approver or management certifies it under Finance → ✅ GRC Certification.';
// Why a bill row cannot be picked — null when it can. One function so the row, the amount line and
// the reason in place of Submit can never disagree.
const billBlock = (b) => b.hold_title ? 'held' : b.claimed_by ? 'taken'
  : b.review_status !== 'approved' ? (b.review_status ?? 'not set')
  : Number(b.outstanding) <= 0.01 ? 'settled' : null;

const TAB_KEYS = ['raise', 'queue', 'to_pay', 'mine'];

export default function Requests() {
  const { user } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const isApprover = APPROVER_ROLES.includes(user?.role);
  // Accounts is deliberately NOT an approver — it EXECUTES what someone else authorised.
  // That segregation is why an approved payment appeared on no screen accounts could open:
  // the queue tab is approver-only, and My Requests filters to what you yourself raised.
  // None of the six open payments was raised by an accounts user, so their whole screen was
  // a form and an empty table while they were the only role that could discharge them.
  const canPay = PAY_ROLES.includes(user?.role);
  const [tab, setTab] = useState(loc.state?.tab || (isApprover ? 'queue' : PAY_ROLES.includes(user?.role) ? 'to_pay' : 'raise'));
  const [open, setOpen] = useState(null);   // request detail id

  // DEEP LINK — /requests?id=<uuid> opens that request's detail, &tab=<key> selects a tab.
  // The approvals hub links here with a plain <a href>, which is a FULL page load: loc.state
  // is undefined on arrival, so state alone could never carry the target and every deep link
  // silently fell through to the default tab. The query string is the only thing that survives.
  // loc.state is still honoured (above) for in-app navigation that does carry it.
  // Params are cleared once handled so a refresh doesn't re-open the modal — same pattern as
  // JobWork.js and Procurement.js.
  useEffect(() => {
    const sp = new URLSearchParams(loc.search);
    const t = sp.get('tab'), id = sp.get('id');
    // 'queue' exists only for approvers — never strand a non-approver on a tab that renders nothing.
    if (t && TAB_KEYS.includes(t) && (t !== 'queue' || isApprover) && (t !== 'to_pay' || canPay)) setTab(t);
    if (id) setOpen(id);
    if (t || id) navigate(loc.pathname, { replace: true });
  }, [loc.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ minHeight: '100vh', background: '#F7F6F3' }}>
      <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
        <h2 style={{ margin: '0 0 4px', color: '#2D2C72', fontSize: 22 }}>💳 Payment Requests</h2>
        <div style={{ display: 'flex', gap: 8, margin: '14px 0' }}>
          <TabBtn k="raise" cur={tab} set={setTab}>✍️ Raise Payment</TabBtn>
          {isApprover && <TabBtn k="queue" cur={tab} set={setTab}>📥 Pending My Approval</TabBtn>}
          {canPay && <TabBtn k="to_pay" cur={tab} set={setTab}>💸 Approved · awaiting payment</TabBtn>}
          <TabBtn k="mine" cur={tab} set={setTab}>📋 My Requests</TabBtn>
        </div>
        {tab === 'raise' && <RaisePayment onDone={() => setTab('mine')} />}
        {tab === 'queue' && isApprover && <ApprovalQueue user={user} onOpen={setOpen} />}
        {(tab === 'to_pay' || tab === 'raise') && canPay && <BillHoldsBanner />}
        {tab === 'to_pay' && canPay && <ToPay onOpen={setOpen} />}
        {tab === 'mine' && <MyRequests user={user} onOpen={setOpen} />}
      </div>
      {open && <RequestDetail id={open} user={user} isApprover={isApprover} onClose={() => setOpen(null)} onChanged={() => setOpen(null)} />}
    </div>
  );
}

const TabBtn = ({ k, cur, set, children }) => (
  <button onClick={() => set(k)} style={{ padding: '8px 16px', border: 'none', background: cur === k ? '#3B3A8F' : '#fff', color: cur === k ? '#fff' : '#555', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{children}</button>
);

// ── RAISE ────────────────────────────────────────────────────────────────────
function RaisePayment({ onDone }) {
  const { user } = useAuth();
  const canAddPayee = PAYEE_ADMIN_ROLES.includes(user?.role);
  const [kind, setKind] = useState('adhoc');   // adhoc | release | pdc
  const [payees, setPayees] = useState([]);
  const [payeeSearch, setPayeeSearch] = useState('');
  const [grns, setGrns] = useState([]);
  const [grnsError, setGrnsError] = useState('');  // a failed load must not read as "nothing to release"
  const [grnSearch, setGrnSearch] = useState('');
  const [bills, setBills] = useState(null);      // invoices on the selected GRN's PO — null = not loaded yet
  const [pos, setPos] = useState([]);           // PDC: approved POs
  const [poInfo, setPoInfo] = useState(null);   // PDC: { po_value, outstanding, payee_name }
  const [exposure, setExposure] = useState(null); // PDC: party-summary strip for the payee
  const [form, setForm] = useState({ payee_id: '', amount: '', due_date: '', purpose: '', grn_id: '', po_no: '', invoice_id: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');
  // Load state for the PDC PO dropdown — see the effect below for why this exists.
  const [posState, setPosState] = useState({ loading: true, error: '', scopeError: '' });

  // Read via the RAISE-gated requests endpoint so every raiser can search/pick
  // (GET /finance/pdc/payees is admin/management/accounts-only and would 403 them).
  const loadPayees = useCallback(() => api.get('/requests/payees').then(r => setPayees(r.data.data || [])).catch(() => {}), []);
  useEffect(() => {
    loadPayees();
    api.get('/requests/grn-options').then(r => { setGrns(r.data.data || []); setGrnsError(''); })
      .catch(e => setGrnsError(e.response?.data?.error || 'The GRN list could not be loaded.'));
    // A FAILED CALL AND AN EMPTY RESULT MUST NOT LOOK THE SAME. The PDC PO dropdown
    // was `.catch(() => {})`, so when the backend returned nothing for ten days the
    // screen showed an empty picker and nobody could tell whether that meant "no
    // approved POs", "the request failed", or "the filter is broken". It was the third.
    //
    // Three distinct states now: loaded-with-rows, loaded-empty (with the reason the
    // server gave, if any), and failed (with the error).
    setPosState({ loading: true, error: '', scopeError: '' });
    api.get('/requests/pdc-pos')
      .then(r => {
        setPos(r.data.data || []);
        setPosState({ loading: false, error: '', scopeError: r.data.scope_error || '' });
      })
      .catch(e => {
        setPos([]);
        setPosState({ loading: false, scopeError: '',
          error: e.response?.data?.error || 'Could not load purchase orders. Reload, or tell IT if it persists.' });
      });
  }, [loadPayees]);

  // PDC: on PO change, fetch outstanding + resolved payee, then the payee's PDC exposure.
  const onPickPo = (po_no) => {
    setForm(f => ({ ...f, po_no }));
    setPoInfo(null); setExposure(null);
    if (!po_no) return;
    api.get('/requests/po-outstanding?po_no=' + encodeURIComponent(po_no)).then(r => {
      const d = r.data.data; setPoInfo(d);
      // party-summary now returns { parties, total } — unwrap to the parties array (was a bare array).
      // BY LINK, never by name: the payee this request would pay. No payee yet means no cheque
      // can be on one, so there is nothing to show; a name search would only find look-alikes.
      if (d.payee_id) api.get('/finance/pdc/party-summary?payee_id=' + encodeURIComponent(d.payee_id)).then(rr => setExposure(rr.data.data?.parties || [])).catch(() => setExposure(null));
      else setExposure([]);
    }).catch(() => {});
  };

  // ── ADDING A PAYEE IS A MASTER WRITE, AND IT WAS A window.prompt ─────────────────────
  // Two problems with that, both seen in production here. prompt() returns null when the
  // user cancels AND when the browser has suppressed dialogs — the "prevent this page from
  // creating additional dialogs" box, which anyone hits after a few — so a suppressed dialog
  // silently did nothing and the person was left believing they had added the payee. And it
  // asked for a name with no sight of the list it was being added to, which is how a register
  // ends up holding one party twice: `Fine threads` and `fine thread`, `mithila knit` and
  // `MITHILA KNIT & CREATION`, both already in the suppliers table for exactly this reason.
  //
  // The panel pre-fills from what is already typed in the search box, shows anything it might
  // already be, and needs a real click. It never refuses — a genuinely new payee whose name
  // resembles an old one must be addable — it only makes the collision visible first.
  const [newPayee, setNewPayee] = useState(null);      // null = panel closed
  const openAddPayee = () => setNewPayee({ name: payeeSearch.trim(), busy: false });
  const confirmAddPayee = async () => {
    const name = (newPayee?.name || '').trim();
    if (!name) { setErr('Enter the payee name.'); return; }
    setNewPayee(n => ({ ...n, busy: true }));
    try { const p = (await api.post('/finance/pdc/payees', { name })).data.data;
      setPayees(ps => ps.some(x => x.id === p.id) ? ps : [...ps, p]);
      setForm(f => ({ ...f, payee_id: p.id })); setPayeeSearch(p.name); setNewPayee(null); setErr(''); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to add payee'); setNewPayee(n => n && ({ ...n, busy: false })); }
  };

  const selectedGrn = grns.find(g => g.grn_id === form.grn_id);
  // ONE PO CAN CARRY SEVERAL BILLS (mig 286). The GRN fixes the PO; the bills on that PO are
  // then fetched so the raiser picks WHICH ONE this release settles. Cleared whenever the GRN
  // changes, so a stale bill from a previously selected GRN can never be submitted.
  useEffect(() => {
    setForm(f => ({ ...f, invoice_id: '' }));
    if (!selectedGrn?.po_no) { setBills(null); return; }
    let live = true;
    api.get(`/requests/invoice-options?po_no=${encodeURIComponent(selectedGrn.po_no)}`)
      .then(r => { if (live) setBills(r.data.data || []); })
      .catch(() => { if (live) setBills([]); });
    return () => { live = false; };
  }, [selectedGrn?.po_no]);   // eslint-disable-line react-hooks/exhaustive-deps
  // THE GRN IS THE CEILING, THE BILL IS THE AMOUNT (ruled 2026-09-02). grnCeiling is what
  // ARRIVED (accepted-qty x rate); the request asks what is OWED — the bill's payable less
  // anything already settled against it. Different questions, so the screen shows both.
  const selectedBill = (bills || []).find(b => b.id === form.invoice_id) || null;
  // A bill that names its own receipts is measured against their SUM (ruled 3 Oct 2026) — the figure
  // the server applies. Only a bill with no receipt on its lines falls back to the picked GRN.
  const grnCeiling = selectedBill && Number(selectedBill.receipts_ceiling) > 0 ? Number(selectedBill.receipts_ceiling)
    : selectedGrn ? Number(selectedGrn.amount) : 0;
  const releaseAmount = selectedBill ? Number(selectedBill.outstanding) : 0;
  // TWO BANDS, NOT ONE (ruled 2026-09-02). Above 2x the receipt is refused; between 1x and 2x is
  // allowed and said. The multiple is 2 and not 1 because po.unit_price is not the rate vendors
  // bill at — the uom-free-text problem, not over-billing — and a 1x rule would refuse 149 of the
  // 254 bills that have a receipt. Mirrors CEILING_MULTIPLE in requests.routes.js; the server
  // refuses either way, this only means the raiser meets it before submitting rather than after.
  const CEILING_MULTIPLE = 2;
  // Over 2x is refused unless management declared an exception for this bill (mig 375).
  const overCeiling = !!selectedBill && !selectedBill.exception_id && releaseAmount > grnCeiling * CEILING_MULTIPLE + 0.01;
  const aboveReceipt = !!selectedBill && !overCeiling && releaseAmount > grnCeiling + 0.01;
  const effAmount = kind === 'release' ? releaseAmount : Number(form.amount || 0);
  const level = effAmount > THRESHOLD ? 'finance_approver → management' : 'finance_approver';
  const pdcOverOutstanding = kind === 'pdc' && poInfo && poInfo.outstanding != null && Number(form.amount || 0) > Number(poInfo.outstanding) + 0.005;

  const filteredPayees = payees.filter(p => !payeeSearch || p.name.toLowerCase().includes(payeeSearch.toLowerCase()));

  const submit = async () => {
    setErr(''); setOkMsg(''); setBusy(true);
    try {
      const body = kind === 'adhoc'
        ? { payment_kind: 'adhoc', payee_id: form.payee_id, amount: Number(form.amount), due_date: form.due_date || null, purpose: form.purpose, submit: true }
        : kind === 'pdc'
        ? { payment_kind: 'pdc', po_no: form.po_no, amount: Number(form.amount), due_date: form.due_date || null, purpose: form.purpose || null, submit: true }
        : { payment_kind: 'release', grn_id: form.grn_id, invoice_id: form.invoice_id, due_date: form.due_date || null, purpose: form.purpose || null, submit: true };
      const r = await api.post('/requests', body);
      setOkMsg(`✅ ${r.data.data.request_no} raised — pending ${r.data.data.current_level}.${r.data.warning ? ' ⚠ ' + r.data.warning : ''}`);
      setForm({ payee_id: '', amount: '', due_date: '', purpose: '', grn_id: '', po_no: '', invoice_id: '' }); setPayeeSearch(''); setPoInfo(null); setExposure(null);
      setTimeout(onDone, 1400);
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const canSubmit = kind === 'adhoc' ? (form.payee_id && Number(form.amount) > 0)
    : kind === 'pdc' ? (form.po_no && Number(form.amount) > 0 && form.due_date)
    : (form.grn_id && form.invoice_id && releaseAmount > 0 && !overCeiling);

  // WHY SUBMIT IS NOT OFFERED — stated where the button would be, never a grey button alone.
  // Same rule as CertificationQueue.js: a disabled control with no explanation is how people
  // conclude the system is broken. Reported 2 Oct as "not able to raise payment request": a bill
  // the client itself refused to pick, an amount line still saying "pick a bill", and nothing else.
  const pickable = (bills || []).filter(b => !billBlock(b));
  const pendingBills = (bills || []).filter(b => billBlock(b) === 'pending');
  const submitBlock = canSubmit ? null
    : kind === 'adhoc' ? (!form.payee_id ? 'Pick a payee from the list.' : 'Enter an amount above zero.')
    : kind === 'pdc' ? (!form.po_no ? 'Select a PO.' : !(Number(form.amount) > 0) ? 'Enter an amount above zero.' : 'Enter the cheque due date.')
    : !form.grn_id ? 'Select a GRN.'
    : bills === null ? 'Loading the bills on this PO…'
    : bills.length === 0 ? `No vendor bill is booked against ${selectedGrn?.po_no} yet — book the invoice first.`
    : pickable.length === 0
      ? (pendingBills.length > 0
          ? `Not a fault — waiting on certification. ${pendingBills.length === bills.length ? 'Every bill' : `${pendingBills.length} of ${bills.length} bills`} on ${selectedGrn?.po_no} ${pendingBills.length === 1 ? 'is' : 'are'} still pending Finance review, and only a certified bill can be released. ${CERTIFIER_HINT}`
          : `No bill on ${selectedGrn?.po_no} can be released — each one's reason is shown on its row.`)
    : !form.invoice_id ? 'Click the bill this release settles.'
    : overCeiling ? `This bill asks ${(releaseAmount / grnCeiling).toFixed(1)} times the goods received (${inr(releaseAmount)} against ${inr(grnCeiling)}). Check it against the paper invoice before paying — usually the bill header, the PO rate or the GRN quantity is keyed wrong, and the fix is to correct it.`
    : null;

  return (
    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E8E7E7', padding: 20, maxWidth: 640 }}>
      {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
      {okMsg && <div style={{ background: '#EAF7EE', color: '#166534', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{okMsg}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <button onClick={() => setKind('adhoc')} style={{ flex: 1, padding: '10px', border: kind === 'adhoc' ? '2px solid #3B3A8F' : '1px solid #ddd', background: kind === 'adhoc' ? '#EEEDF8' : '#fff', borderRadius: 8, cursor: 'pointer', fontWeight: 700, color: kind === 'adhoc' ? '#3B3A8F' : '#666' }}>Ad-hoc payment<div style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>courier, electrician, advance</div></button>
        <button onClick={() => setKind('release')} style={{ flex: 1, padding: '10px', border: kind === 'release' ? '2px solid #3B3A8F' : '1px solid #ddd', background: kind === 'release' ? '#EEEDF8' : '#fff', borderRadius: 8, cursor: 'pointer', fontWeight: 700, color: kind === 'release' ? '#3B3A8F' : '#666' }}>Release against PO/GRN<div style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>amount from the system</div></button>
        <button onClick={() => setKind('pdc')} style={{ flex: 1, padding: '10px', border: kind === 'pdc' ? '2px solid #3B3A8F' : '1px solid #ddd', background: kind === 'pdc' ? '#EEEDF8' : '#fff', borderRadius: 8, cursor: 'pointer', fontWeight: 700, color: kind === 'pdc' ? '#3B3A8F' : '#666' }}>PDC against PO<div style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>post-dated cheque</div></button>
      </div>

      {kind === 'adhoc' ? (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Payee * <span style={{ color: '#999', fontWeight: 400, textTransform: 'none' }}>(from the master — no free text)</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...inputStyle, flex: 1 }} value={payeeSearch} onChange={e => { setPayeeSearch(e.target.value); setForm(f => ({ ...f, payee_id: '' })); }} placeholder="🔍 search payees…" />
              {canAddPayee && <button type="button" onClick={openAddPayee} style={{ padding: '0 12px', background: '#EEEDF8', color: '#3B3A8F', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>＋ New</button>}
            </div>
            <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, marginTop: 6 }}>
              {filteredPayees.length === 0 ? <div style={{ padding: 10, color: '#aaa', fontSize: 12 }}>No payees match{canAddPayee ? ' — add one.' : ' — ask Accounts to add this payee.'}</div>
                : filteredPayees.map(p => (
                  <div key={p.id} onClick={() => { setForm(f => ({ ...f, payee_id: p.id })); setPayeeSearch(p.name); }}
                    style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, background: form.payee_id === p.id ? '#EEEDF8' : '#fff', borderBottom: '1px solid #f5f5f5', fontWeight: form.payee_id === p.id ? 700 : 400 }}>
                    {form.payee_id === p.id ? '✓ ' : ''}{p.name}
                  </div>
                ))}
            </div>
            {newPayee && (() => {
              const hits = nearMatches(newPayee.name, payees);
              return (
                <div style={{ marginTop: 8, border: '1px solid #E3E2F0', background: '#F7F7FB', borderRadius: 6, padding: '10px 12px' }}>
                  <label style={labelStyle}>New payee name — added to the shared master</label>
                  <input autoFocus style={inputStyle} value={newPayee.name}
                    onChange={e => setNewPayee(n => ({ ...n, name: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter' && !newPayee.busy) confirmAddPayee(); }}
                    placeholder="Exactly as it should appear on a cheque" />
                  {hits.length > 0 && (
                    <div style={{ marginTop: 8, background: '#FFF7ED', border: '1px solid #E0A030', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#7C4A03' }}>
                      <strong>Already in the master{hits.length > 1 ? ' (closest first)' : ''}:</strong>
                      {hits.map(h => (
                        <div key={h.item.id} style={{ marginTop: 4 }}>
                          <button type="button" onClick={() => { setForm(f => ({ ...f, payee_id: h.item.id })); setPayeeSearch(h.name); setNewPayee(null); }}
                            style={{ background: '#fff', border: '1px solid #E0A030', borderRadius: 5, padding: '3px 9px', cursor: 'pointer', fontSize: 12, color: '#7C4A03', fontWeight: 600 }}>
                            use “{h.name}”
                          </button>
                          <span style={{ marginLeft: 6, color: '#9a6a1a' }}>{h.why}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: 6 }}>Add it anyway only if this is a different party.</div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button type="button" disabled={newPayee.busy} onClick={confirmAddPayee}
                      style={{ padding: '7px 16px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: newPayee.busy ? 'wait' : 'pointer', opacity: newPayee.busy ? 0.6 : 1 }}>
                      {newPayee.busy ? 'Adding…' : 'Add payee'}
                    </button>
                    <button type="button" onClick={() => setNewPayee(null)}
                      style={{ padding: '7px 16px', background: '#f1f1f4', border: 'none', borderRadius: 6, fontSize: 12.5, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              );
            })()}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Amount (₹) *</label><input type="number" style={inputStyle} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
            <div><label style={labelStyle}>Due date</label><input type="date" style={inputStyle} value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
          </div>
          <div style={{ marginTop: 12 }}><label style={labelStyle}>Purpose / description</label><input style={inputStyle} value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="e.g. Electrician — Line 3 wiring" /></div>
        </>
      ) : kind === 'pdc' ? (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>PO * <span style={{ color: '#999', fontWeight: 400, textTransform: 'none' }}>(mandatory — payee is the PO's supplier)</span></label>
            <select style={inputStyle} value={form.po_no} onChange={e => onPickPo(e.target.value)} disabled={posState.loading || !!posState.error}>
              <option value="">{posState.loading ? 'loading purchase orders…' : '— select a PO —'}</option>
              {pos.map(p => <option key={p.po_no} value={p.po_no}>{p.po_no} · {p.supplier_name || '—'} · {inr(p.po_value)}</option>)}
            </select>
            {/* THREE distinct states, deliberately. "Empty" and "broken" used to render
                identically, which is how a dropdown that never worked survived ten days. */}
            {posState.error ? (
              <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4, fontWeight: 600 }}>
                ⚠ {posState.error}
              </div>
            ) : posState.scopeError ? (
              <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4, fontWeight: 600 }}>
                ⚠ {posState.scopeError}
              </div>
            ) : !posState.loading && pos.length === 0 ? (
              <div style={{ fontSize: 11, color: '#B45309', marginTop: 4 }}>
                No approved POs for your company. (The list loaded correctly — there are none to show.)
              </div>
            ) : null}
          </div>
          {poInfo && (
            <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 }}>
              <div>Payee (PO supplier): <strong>{poInfo.payee_name || '—'}</strong> <span style={{ color: '#999', fontSize: 11 }}>(auto — added to the payee master on approval)</span></div>
              <div style={{ marginTop: 4 }}>PO value: <strong>{inr(poInfo.po_value)}</strong> · Outstanding: <strong style={{ color: poInfo.outstanding <= 0 ? '#A32D2D' : '#166534' }}>{poInfo.outstanding == null ? '—' : inr(poInfo.outstanding)}</strong></div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Amount (₹) *</label><input type="number" style={inputStyle} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
            <div><label style={labelStyle}>Cheque due date * <span style={{ color: '#999', fontWeight: 400, textTransform: 'none' }}>(post-dated)</span></label><input type="date" style={inputStyle} value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
          </div>
          {pdcOverOutstanding && <div style={{ background: '#FFF4E5', color: '#8A5A00', border: '1px solid #F3C77A', padding: '8px 12px', borderRadius: 6, marginTop: 10, fontSize: 12 }}>⚠ Amount exceeds this PO's outstanding ({inr(poInfo.outstanding)}). You can still submit — this is a warning.</div>}
          <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>Accounts fill the cheque number and bank account later, when the physical cheque is written.</div>
          {exposure && exposure.length > 0 && (
            <div style={{ background: '#F4F5FA', borderRadius: 8, padding: '10px 12px', fontSize: 12, marginTop: 12 }}>
              <div style={{ fontWeight: 700, color: '#3B3A8F', marginBottom: 4 }}>This payee's PDC exposure</div>
              {exposure.map((g, i) => (
                <div key={i} style={{ color: '#555' }}>
                  {g.party_label || poInfo?.payee_name}: cleared {inr(g.cleared?.total)} · balance-to-clear <strong>{inr(g.balance_to_clear?.total)}</strong>{g.stopped?.total ? ` · stopped ${inr(g.stopped.total)}` : ''}
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 12 }}><label style={labelStyle}>Purpose / note (optional)</label><input style={inputStyle} value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} /></div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>GRN (goods received) *</label>
            {/* EVERY RECEIPT IS LISTED (15 Oct 2026). The picker used to show the 200 newest, and every payable
                bill sat on an older one. Releasable GRNs come first; the rest are greyed with the reason, so a GRN
                nobody can pay yet says who has to act instead of looking lost. The search carries the longer list. */}
            <input style={{ ...inputStyle, marginBottom: 6 }} value={grnSearch} onChange={e => setGrnSearch(e.target.value)}
              placeholder="Search GRN, vendor, PO or GPO" />
            {(() => {
              const needle = grnSearch.trim().toLowerCase();
              const shown = needle ? grns.filter(g => g.grn_id === form.grn_id || [g.grn_no, g.vendor, g.po_no, g.gpo_no].some(v => String(v || '').toLowerCase().includes(needle))) : grns;   // the chosen GRN stays in the list while the search changes
              const ready = shown.filter(g => g.actionable), waiting = shown.filter(g => !g.actionable);
              return (
                <>
                  <select style={inputStyle} value={form.grn_id} onChange={e => setForm(f => ({ ...f, grn_id: e.target.value }))}>
                    <option value="">— select a GRN —</option>
                    {ready.length > 0 && <optgroup label={`Ready to release (${ready.length})`}>
                      {ready.map(g => <option key={g.grn_id} value={g.grn_id}>{g.grn_no} · {g.vendor || '—'} · {inr(g.amount)}</option>)}
                    </optgroup>}
                    {waiting.length > 0 && <optgroup label={`Not releasable yet (${waiting.length})`}>
                      {waiting.map(g => <option key={g.grn_id} value={g.grn_id} disabled>{g.grn_no} · {g.vendor || '—'} — {g.reason}</option>)}
                    </optgroup>}
                  </select>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    {grnsError ? <span style={{ color: '#A32D2D' }}>{grnsError}</span>
                      : grns.length === 0 ? 'No goods receipts are on record for your company.'
                      : needle && shown.length === 0 ? `No GRN matches "${grnSearch.trim()}".`
                      : `${ready.length} ready to release${waiting.length ? `; ${waiting.length} listed with the reason they cannot be released yet` : ''}.`}
                  </div>
                </>
              );
            })()}
          </div>
          <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
            <div>Payee (from PO vendor): <strong>{selectedGrn?.vendor || '—'}</strong></div>
            <div style={{ marginTop: 4 }}>GRN receipt (accepted-qty × rate) — the <strong>ceiling</strong>: <strong>{inr(grnCeiling)}</strong></div>
            <div style={{ marginTop: 4 }}>Amount requested (from the bill, <strong>read-only</strong>): <strong style={{ color: releaseAmount > 0 ? '#166534' : '#999' }}>{selectedBill ? inr(releaseAmount) : bills && bills.length > 0 && pickable.length === 0 ? 'none — no bill on this PO can be released yet (see below)' : '— pick a bill —'}</strong></div>
            {selectedGrn?.po_no && <div style={{ marginTop: 4, color: '#777' }}>PO: <strong>{selectedGrn.po_no}</strong></div>}
          </div>

          {/* WHICH BILL — one PO can carry several. The claim and the payable differ, and the
              debit note is named so the difference is explained rather than merely shown. */}
          {selectedGrn && (
            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>Bill being settled * <span style={{ color: '#999', fontWeight: 400, textTransform: 'none' }}>(a vendor is paid against a bill — one PO can carry several)</span></label>
              {bills === null ? <div style={{ fontSize: 12, color: '#888', padding: '8px 0' }}>Loading bills…</div>
                : bills.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#B45309', border: '1px solid #F5C77E', background: '#FEF3E2', borderRadius: 6, padding: '9px 11px' }}>
                    No vendor bill is booked against {selectedGrn.po_no} yet. A release names the bill it settles, so book the invoice first.
                  </div>
                ) : (
                  <div style={{ border: '1px solid #eee', borderRadius: 6, overflow: 'hidden' }}>
                    {bills.map(b => {
                      const why = billBlock(b);
                      const taken = why === 'taken';
                      const notApproved = !taken && why !== 'settled' && why !== 'held' && why !== null;
                      const settled = why === 'settled';
                      const blocked = why !== null;
                      const chosen = form.invoice_id === b.id;
                      return (
                        <div key={b.id}
                          onClick={() => { if (!blocked) setForm(f => ({ ...f, invoice_id: b.id })); }}
                          style={{ padding: '9px 11px', borderBottom: '1px solid #f5f5f5', fontSize: 12.5,
                                   cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.55 : 1,
                                   background: chosen ? '#EEEDF8' : '#fff' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                            <span style={{ fontWeight: chosen ? 700 : 600 }}>{chosen ? '✓ ' : ''}{b.invoice_no || '(no number)'}</span>
                            <span style={{ color: '#888' }}>{b.invoice_date ? fmtD(b.invoice_date) : ''}</span>
                          </div>
                          <div style={{ marginTop: 3, color: '#555' }}>
                            Claim {inr(b.claim)} · payable {inr(b.payable)}
                            {Number(b.debit_note_total) > 0 && <span style={{ color: '#A32D2D' }}> · less {inr(b.debit_note_total)} debit note {b.debit_no ? `(${b.debit_no})` : ''}</span>}
                            {Number(b.paid) > 0 && <span> · paid {inr(b.paid)}</span>}
                            {' · '}<strong style={{ color: '#166534' }}>outstanding {inr(b.outstanding)}</strong>
                          </div>
                          {why === 'held' && <div style={{ marginTop: 3, color: '#A32D2D', fontWeight: 700 }}>HELD — {b.hold_title}. It cannot be released until the hold is resolved (Finance → Purchase invoices).</div>}
                          {taken && <div style={{ marginTop: 3, color: '#A32D2D', fontWeight: 600 }}>Already on {b.claimed_by} ({b.claimed_status}) — one bill, one live request.</div>}
                          {notApproved && (why === 'rejected'
                        ? <div style={{ marginTop: 3, color: '#A32D2D', fontWeight: 600 }}>Bill was rejected by Finance — it cannot be released. Have it corrected and re-booked.</div>
                        : <div style={{ marginTop: 3, color: '#B45309', fontWeight: 600 }}>Not certified yet (Finance review: {why}) — cannot be released until it is. {CERTIFIER_HINT}</div>)}
                          {!taken && !notApproved && settled && <div style={{ marginTop: 3, color: '#777', fontWeight: 600 }}>Settled in full — nothing left to release.</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              {/* The GRN ceiling. Sourcing the amount from the bill drops a guard the old
                  formula had for free — it could not exceed what arrived, because it WAS what
                  arrived. Surfaced here so the raiser meets it before submitting; the route
                  refuses it either way. Both figures are named, because a short delivery and an
                  over-bill are different problems with different owners. */}
              {overCeiling && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#A32D2D', background: '#FCEBEB', border: '1px solid #f0cccc', borderRadius: 6, padding: '9px 11px' }}>
                  <strong>Not releasable.</strong> {selectedBill.invoice_no} asks {inr(releaseAmount)} but GRN {selectedGrn?.grn_no || ''} received only {inr(grnCeiling)} — more than {CEILING_MULTIPLE}×.
                  That is a short delivery or an over-bill, not a rate mismatch — correct one of them first.
                </div>
              )}
              {aboveReceipt && (
                <div style={{ marginTop: 8, fontSize: 11.5, color: '#8a6d3b', background: '#FEF9E7', border: '1px solid #F0E0A8', borderRadius: 6, padding: '8px 10px' }}>
                  {selectedBill.invoice_no} asks {inr(releaseAmount)} against a GRN receipt of {inr(grnCeiling)}.
                  Usually the PO rate rather than the bill — check it is not a short delivery. You can still raise it.
                </div>
              )}
            </div>
          )}
          <div style={{ marginTop: 12 }}><label style={labelStyle}>Purpose / note (optional)</label><input style={inputStyle} value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} /></div>
        </>
      )}

      {/* Approval-level preview BEFORE submit */}
      {effAmount > 0 && (
        <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 8, background: effAmount > THRESHOLD ? '#FEF3E2' : '#EEF', border: '1px solid ' + (effAmount > THRESHOLD ? '#F5C77E' : '#ccd'), fontSize: 12 }}>
          {inr(effAmount)} → approval chain: <strong>{level}</strong> {effAmount > THRESHOLD ? `(over ${inr(THRESHOLD)} → two-level)` : `(≤ ${inr(THRESHOLD)} → one-level)`}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 16 }}>
        {/* The refusal goes WHERE THE BUTTON WOULD BE — never a grey button with nothing beside it. */}
        {submitBlock && <div style={{ flex: 1, fontSize: 12, color: '#7C4A03', background: '#FFF7ED', border: '1px solid #F0D3A0', borderRadius: 6, padding: '8px 11px' }}>{submitBlock}</div>}
        <button disabled={busy || !canSubmit} onClick={submit} style={{ padding: '9px 22px', background: canSubmit ? '#3B3A8F' : '#ccc', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 700 }}>{busy ? 'Submitting…' : 'Submit for approval'}</button>
      </div>
    </div>
  );
}

// ── APPROVAL QUEUE ───────────────────────────────────────────────────────────
function ApprovalQueue({ user, onOpen }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/requests?pending_for_me=1').then(r => setRows(r.data.data || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E8E7E7', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', fontSize: 12, color: '#888', borderBottom: '1px solid #eee' }}>Requests waiting on <strong>{user?.role}</strong> — your own requests never appear here (a different approver must sign them off).</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#F7F6F3' }}>{['Request', 'Type', 'Payee', 'Amount', 'Raised by', 'Waiting on', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 24 }}>Nothing waiting on your approval. 🎉</td></tr>
            : rows.map(r => (
              <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(r.id)}>
                <td style={{ ...td, fontWeight: 700, color: '#3B3A8F' }}>{r.request_no}</td>
                <td style={td}>{r.payment_kind}</td>
                <td style={td}>{r.payee_name || r.po_no || '—'}</td>
                <td style={{ ...td, fontWeight: 700 }}>{inr(r.amount)}</td>
                <td style={td}>{r.raised_by_name}</td>
                <td style={{ ...td, color: /final approver|Waiting for Rajan/.test(r.waiting_on || '') ? '#7A5A00' : '#555' }}>{r.waiting_on || r.purpose || '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}><button style={{ padding: '4px 12px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Review →</button></td>
              </tr>
            ))}
        </tbody>
      </table>
      <ListFooter shown={rows.length} total={rows.length} noun="pending" />
    </div>
  );
}

// ── APPROVED · AWAITING PAYMENT ──────────────────────────────────────────────
// The back half of the spine. status='approved' reads as finished, which is exactly why six
// fully-authorised payments sat up to 34 days with their payables still open and nobody
// looking: approval removes a request from the approver's queue and it lands in no other.
//
// type=payment is LOAD-BEARING. requests also holds indent_general, which becomes a GSPO and
// is never "paid" — 35 of those would otherwise pad this list with rows the Pay button cannot
// act on.
//
// No backend change: GET /requests already supports both filters and already returns paid_at.
function ToPay({ onOpen }) {
  const [rows, setRows] = useState([]);
  // ONE TRANSFER, SEVERAL REQUESTS OF ONE PAYEE (3 Oct 2026). Ticking is limited to one payee at a
  // time; the server checks the same and records one ledger row per request, all or nothing.
  const [sel, setSel] = useState([]);
  const [batch, setBatch] = useState({ payment_mode: 'neft', reference_no: '', bank_account_id: '', payment_date: '', remarks: '' });
  const [banks, setBanks] = useState([]);
  const [bErr, setBErr] = useState(''); const [bOk, setBOk] = useState(''); const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get('/requests?type=payment&status=approved')
    .then(r => setRows((r.data.data || []).filter(x => !x.paid_at)))
    .catch(() => setRows([])), []);
  useEffect(() => { load(); api.get('/finance/bank-accounts').then(r => setBanks(r.data.data || [])).catch(() => setBanks([])); }, [load]);
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const ageOf = (r) => Math.floor((Date.now() - new Date(r.updated_at || r.created_at)) / 86400000);
  const payeeOf = (r) => (r.payee_name || '').trim().toLowerCase();
  const batchable = (r) => ['release', 'adhoc'].includes(r.payment_kind);
  const chosen = rows.filter(r => sel.includes(r.id));
  const lockedPayee = chosen.length ? payeeOf(chosen[0]) : null;
  const toggle = (r) => setSel(s => s.includes(r.id) ? s.filter(x => x !== r.id) : [...s, r.id]);
  const chosenTotal = chosen.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const payBatch = async () => {
    setBErr(''); setBOk('');
    if (!batch.reference_no.trim()) return setBErr('The UTR / cheque number is required — it is what makes this one payment.');
    if (!batch.bank_account_id) return setBErr('Select the bank account debited.');
    setBusy(true);
    try {
      const r = await api.post('/requests/pay-batch', { ...batch, request_ids: sel });
      setBOk(`✅ ${r.data.data.length} requests paid under ${batch.reference_no} · ${inr(r.data.total)}`);
      setSel([]); setBatch(b => ({ ...b, reference_no: '', remarks: '' })); load();
    } catch (e) { setBErr(e.response?.data?.error || 'Failed — nothing was recorded.'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E8E7E7', overflow: 'hidden' }}>
      {rows.length > 0 && (
        <div style={{ padding: '10px 14px', background: '#FFF7ED', borderBottom: '1px solid #fed7aa', fontSize: 12, color: '#8A5A00' }}>
          <strong>{rows.length}</strong> authorised payment{rows.length === 1 ? '' : 's'} awaiting discharge · <strong>{inr(total)}</strong>.
          Each has an open payable behind it until it is paid. Tick several for one payee to record them under one UTR.
        </div>
      )}
      {bOk && <div style={{ background: '#EAF7EE', color: '#166534', padding: '8px 14px', fontSize: 13 }}>{bOk}</div>}
      {chosen.length >= 2 && (
        <div style={{ padding: 12, background: '#F6FAFF', borderBottom: '1px solid #C4DAF2' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1D4E89', textTransform: 'uppercase', marginBottom: 8 }}>
            One payment · {chosen.length} requests · {chosen[0].payee_name} · {inr(chosenTotal)} — one ledger row per request</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <select value={batch.payment_mode} onChange={e => setBatch(b => ({ ...b, payment_mode: e.target.value }))} style={{ ...inputStyle, flex: '0 0 120px' }}>
              {PAY_MODES.filter(m => m !== 'cash').map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
            <input value={batch.reference_no} onChange={e => setBatch(b => ({ ...b, reference_no: e.target.value }))} placeholder="UTR / cheque no *" style={{ ...inputStyle, flex: 1, minWidth: 160 }} />
            <input type="date" value={batch.payment_date} max={new Date().toISOString().slice(0, 10)} onChange={e => setBatch(b => ({ ...b, payment_date: e.target.value }))} style={{ ...inputStyle, flex: '0 0 150px' }} />
            <select value={batch.bank_account_id} onChange={e => setBatch(b => ({ ...b, bank_account_id: e.target.value }))} style={{ ...inputStyle, flex: 1, minWidth: 180 }}>
              <option value="">Bank account debited *</option>
              {banks.map(b => <option key={b.id} value={b.id}>{[b.bank_name, b.account_number].filter(Boolean).join(' · ')}</option>)}
            </select>
          </div>
          {bErr && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 8, fontSize: 12 }}>{bErr}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => setSel([])} style={{ padding: '8px 14px', background: 'transparent', border: '1px solid #ccc', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Clear</button>
            <button disabled={busy} onClick={payBatch} style={{ padding: '8px 18px', background: '#1D4E89', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>💰 Record {chosen.length} payments</button>
          </div>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#F7F6F3' }}>{['', 'Request', 'Type', 'Payee', 'Amount', 'Raised by', 'Approved', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 24 }}>Nothing awaiting payment.</td></tr>
            : rows.map(r => {
              const age = ageOf(r);
              const canTick = batchable(r) && (!lockedPayee || lockedPayee === payeeOf(r));
              return (
                <tr key={r.id} style={{ cursor: 'pointer', background: sel.includes(r.id) ? '#EEF4FC' : undefined }} onClick={() => onOpen(r.id)}>
                  <td style={td} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={sel.includes(r.id)} disabled={!canTick && !sel.includes(r.id)} onChange={() => toggle(r)}
                      title={!batchable(r) ? 'Only release and ad-hoc requests are paid together' : !canTick ? 'One payment pays one payee — clear the ticks to start another' : 'Pay with others of this payee under one UTR'} />
                  </td>
                  <td style={{ ...td, fontWeight: 700, color: '#3B3A8F' }}>{r.request_no}</td>
                  <td style={td}>{r.payment_kind}</td>
                  <td style={td}>{r.payee_name || r.po_no || '—'}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{inr(r.amount)}</td>
                  <td style={td}>{r.raised_by_name || '—'}</td>
                  {/* Age since the LAST write, which for an approved-and-untouched request is
                      the approval itself. Coloured past a fortnight — 34 days is not 3. */}
                  <td style={{ ...td, fontWeight: 700, color: age >= 14 ? '#A32D2D' : age >= 7 ? '#B45309' : '#888' }}>{age}d ago</td>
                  <td style={{ ...td, textAlign: 'right', color: '#888' }}>Pay →</td>
                </tr>
              );
            })}
        </tbody>
      </table>
      <ListFooter shown={rows.length} total={rows.length} noun="awaiting payment" />
    </div>
  );
}

// ── MY REQUESTS ──────────────────────────────────────────────────────────────
function MyRequests({ onOpen }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/requests?mine=1').then(r => setRows(r.data.data || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E8E7E7', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#F7F6F3' }}>{['Request', 'Type', 'Payee', 'Amount', 'Status', 'Waiting on', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 24 }}>You haven’t raised any requests.</td></tr>
            : rows.map(r => (
              <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(r.id)}>
                <td style={{ ...td, fontWeight: 700, color: '#3B3A8F' }}>{r.request_no}</td>
                <td style={td}>{r.payment_kind}</td>
                <td style={td}>{r.payee_name || r.po_no || '—'}</td>
                <td style={{ ...td, fontWeight: 700 }}>{inr(r.amount)}</td>
                <td style={td}>{badge(r.status)}</td>
                <td style={td}>{r.status === 'pending' ? (r.waiting_on || r.current_level) : '—'}</td>
                <td style={{ ...td, textAlign: 'right', color: '#888' }}>View →</td>
              </tr>
            ))}
        </tbody>
      </table>
      <ListFooter shown={rows.length} total={rows.length} noun="requests" />
    </div>
  );
}

// ── DETAIL + decide ──────────────────────────────────────────────────────────
function RequestDetail({ id, user, isApprover, onClose, onChanged }) {
  const [r, setR] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [pay, setPay] = useState({
    payment_mode: 'cash', reference_no: '', remarks: '',
    bank_account_id: '', payment_date: '',
  });
  // Banks for "bank debited". Cash needs none — 43 of the 45 cash disbursements on record quote
  // no account and no UTR, because a handover has neither (mig 287).
  const [payBanks, setPayBanks] = useState([]);
  useEffect(() => { api.get('/finance/bank-accounts').then(r => setPayBanks(r.data.data || [])).catch(() => setPayBanks([])); }, []);
  const payIsCash = pay.payment_mode === 'cash';
  const load = useCallback(() => api.get('/requests/' + id).then(x => setR(x.data.data)).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);
  if (!r) return null;

  // The backend blocks raiser==approver; mirror it in the UI so we never even offer it.
  // Mirrors the /decision gate: the level's role; admin anywhere; management also at finance_approver and,
  // to RECOMMEND, at final_approver (mig 379), where Rajan decides.
  const canDecide = isApprover && r.status === 'pending' && r.raised_by !== user?.id && (
    r.current_level === user?.role || user?.role === 'admin' || r.i_am_final_approver
    || (user?.role === 'management' && ['finance_approver', 'final_approver'].includes(r.current_level)));
  const recommendOnly = r.current_level === 'final_approver' && !r.i_am_final_approver;
  const isOwnPending = r.status === 'pending' && r.raised_by === user?.id;
  // Settlement is the executor's step — accounts/admin only, and only once approved.
  const canPay = PAY_ROLES.includes(user?.role) && r.status === 'approved';
  // A general-store INDENT is not a payment voucher: it has no payment_kind and no payee, and
  // its substance is the line items. Describing it with payment vocabulary ("null · ₹X · Payee")
  // told the approver nothing about what was being ordered.
  const isIndent = r.type === 'indent_general';
  const kindLabel = isIndent ? 'General-store indent' : (r.payment_kind || 'Payment');
  const lines = r.lines || [];
  const lineTotal = lines.reduce((s, l) => s + Number(l.amount || 0), 0);

  const decide = async (decision) => {
    if (decision === 'rejected' && !remarks.trim()) { setErr('Remarks are required to reject.'); return; }
    setBusy(true); setErr('');
    try { const res = await api.patch(`/requests/${id}/decision`, { decision, remarks: remarks || null });
      if (res.data.payable_invoice_no) alert(`✅ Approved. Payable ${res.data.payable_invoice_no} created — the Payables tile is now live.`);
      onChanged();
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); setBusy(false); }
  };

  const recordPayment = async () => {
    setBusy(true); setErr('');
    // Full discharge only — amount is fixed to the approved amount, not typed here.
    if (!payIsCash && !pay.bank_account_id) { setErr('Select the bank account debited.'); setBusy(false); return; }
    if (!payIsCash && !pay.reference_no.trim()) { setErr('A UTR / cheque number is required for a non-cash payment.'); setBusy(false); return; }
    try { const res = await api.patch(`/requests/${id}/pay`, {
        payment_mode: pay.payment_mode, reference_no: pay.reference_no || null, remarks: pay.remarks || null,
        payment_date: pay.payment_date,
        // cash debits no account; the ledger's CHECK refuses a bank on a cash row (mig 287)
        bank_account_id: payIsCash ? null : pay.bank_account_id });
      alert(`✅ Paid. Payable ${res.data.payable_invoice_no} closed — the request is now settled.`);
      onChanged();
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 560, maxWidth: '92vw', margin: 'auto' }}>
        <div style={{ background: '#2D2C72', padding: '14px 22px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>{r.request_no} {badge(r.status)}</h3>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>{kindLabel} · {inr(r.amount)}</div></div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 22 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13, marginBottom: 14 }}>
            <div><span style={{ color: '#888' }}>{isIndent ? 'Department: ' : 'Payee: '}</span><strong>{(isIndent ? r.dept : (r.payee_name || r.po_no)) || '—'}</strong></div>
            <div><span style={{ color: '#888' }}>Raised by: </span><strong>{r.raised_by_name}</strong></div>
            <div><span style={{ color: '#888' }}>Due: </span><strong>{r.due_date ? String(r.due_date).slice(0, 10) : '—'}</strong></div>
            <div><span style={{ color: '#888' }}>Waiting on: </span><strong>{r.status === 'pending' ? (r.waiting_on || r.current_level) : '—'}</strong></div>
            <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#888' }}>Purpose: </span>{r.purpose || '—'}</div>
          </div>

          {/* Settlement summary — visible to EVERYONE once paid (raiser, approver, mgmt, accounts) */}
          {r.status === 'paid' && (
            <div style={{ background: '#E7F0FB', border: '1px solid #C4DAF2', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#1D4E89' }}>
              💰 <strong>Paid · {inr(r.paid_amount)} · {(r.payment_mode || '').toUpperCase()}</strong>
              {' · '}{r.paid_at ? new Date(r.paid_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              {' · by '}{r.paid_by_name || '—'}
              {r.reference_no ? ` · ref ${r.reference_no}` : ''}
              {r.settle_remarks ? <div style={{ marginTop: 3, fontStyle: 'italic', color: '#3a6ea5' }}>“{r.settle_remarks}”</div> : null}
            </div>
          )}

          {/* Line items — the API has always returned r.lines; the modal bound them to nothing,
              so an indent's actual contents were invisible to the person approving it. Rendered
              for ANY request that carries lines (a payment request has one, describing itself);
              columns that genuinely don't apply show — rather than a fabricated 0. */}
          {lines.length > 0 && (<>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', marginBottom: 6 }}>
              Items ({lines.length})
            </div>
            <div style={{ marginBottom: 14, border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#FAFAFA' }}>
                  {['#', 'Item', 'Qty', 'Unit', 'Rate', 'Amount'].map((h, i) => (
                    <th key={h} style={{ ...th, textAlign: i >= 2 ? 'right' : 'left', padding: '7px 9px' }}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={l.id || i}>
                      <td style={{ ...td, color: '#aaa' }}>{l.sno ?? i + 1}</td>
                      <td style={{ ...td, color: '#333' }}>{l.item_desc || '—'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{l.qty != null ? Number(l.qty).toLocaleString('en-IN') : '—'}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#888' }}>{l.unit || '—'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{l.rate != null ? inr(l.rate) : '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{l.amount != null ? inr(l.amount) : '—'}</td>
                    </tr>
                  ))}
                  {lines.length > 1 && (
                    <tr style={{ background: '#F7F6F3' }}>
                      <td style={{ ...td, fontWeight: 700 }} colSpan={5}>Total</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#3B3A8F' }}>{inr(lineTotal)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>)}

          {/* Approval trail — append-only */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', marginBottom: 6 }}>Approval trail</div>
          {(r.approvals || []).length === 0 ? <div style={{ fontSize: 12, color: '#aaa', marginBottom: 12 }}>No decisions yet.</div>
            : <div style={{ marginBottom: 14 }}>{r.approvals.map(a => {
                const isPaid = a.decision === 'paid';
                // A RECOMMENDATION and THE FINAL ACT read differently (mig 379).
                const isRec = a.decision === 'recommend';
                const color = isPaid ? '#1D4E89' : isRec ? '#B45309' : a.decision === 'approved' ? '#166534' : a.decision === 'amended' ? '#777' : '#A32D2D';
                const icon = isPaid ? '💰' : isRec ? '👍' : a.decision === 'approved' ? '✅' : a.decision === 'amended' ? '📝' : '❌';
                const what = isRec ? 'Recommended' : a.decision === 'approved' && a.level === 'final_approver' ? 'Final approval' : null;
                return (
                <div key={a.id} style={{ fontSize: 12, padding: '6px 10px', borderLeft: '3px solid ' + color, background: '#FAFAFA', marginBottom: 4 }}>
                  <strong>{icon} {what || a.level}</strong> — {a.decided_by_name} · {new Date(a.decided_at).toLocaleString('en-IN')}{a.remarks ? ` · “${a.remarks}”` : ''}
                </div>); })}</div>}

          {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{err}</div>}
          {isOwnPending && <div style={{ background: '#FFF8E6', color: '#9A6B00', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>You raised this request — it must be approved by someone else (you cannot approve your own).</div>}

          {/* Record Payment — executor (accounts/admin) only, once approved. Full discharge. */}
          {canPay && (
            <div style={{ border: '1px solid #C4DAF2', borderRadius: 8, padding: 12, marginBottom: 4, background: '#F6FAFF' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1D4E89', textTransform: 'uppercase', marginBottom: 8 }}>Record payment · settles {inr(r.amount)} in full</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select value={pay.payment_mode} onChange={e => setPay(p => ({ ...p, payment_mode: e.target.value }))} style={{ ...inputStyle, flex: '0 0 130px' }}>
                  {PAY_MODES.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                </select>
                <input value={pay.reference_no} onChange={e => setPay(p => ({ ...p, reference_no: e.target.value }))}
                       placeholder={payIsCash ? 'Reference (optional for cash)' : 'Cheque no / UTR / txn id *'} style={{ ...inputStyle, flex: 1 }} />
              </div>
              {/* Bank debited + payment date — the ledger's own columns (mig 287). There is
                  deliberately NO amount box here: a request is discharged for its approved
                  figure, and a second place to type an amount is what this whole change set
                  exists to remove. */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input type="date" value={pay.payment_date} max={new Date().toISOString().slice(0, 10)}
                       onChange={e => setPay(p => ({ ...p, payment_date: e.target.value }))} style={{ ...inputStyle, flex: '0 0 150px' }} />
                {payIsCash
                  ? <div style={{ ...inputStyle, flex: 1, color: '#777', background: '#F4F4F4' }}>Cash — no bank account debited</div>
                  : <select value={pay.bank_account_id} onChange={e => setPay(p => ({ ...p, bank_account_id: e.target.value }))} style={{ ...inputStyle, flex: 1 }}>
                      <option value="">Select the bank account debited *</option>
                      {payBanks.map(b => <option key={b.id} value={b.id}>{[b.bank_name, b.account_number].filter(Boolean).join(' · ')}</option>)}
                    </select>}
              </div>
              <input value={pay.remarks} onChange={e => setPay(p => ({ ...p, remarks: e.target.value }))} placeholder="Remarks (optional)" style={{ ...inputStyle, marginBottom: 8 }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button disabled={busy} onClick={recordPayment} style={{ padding: '9px 22px', background: '#1D4E89', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>💰 Record Payment</button>
              </div>
            </div>
          )}

          {canDecide && (<>
            <textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks (required to reject)…" style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, height: 44, resize: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button disabled={busy} onClick={() => decide('rejected')} style={{ padding: '9px 18px', background: '#E22D2D', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>❌ Reject</button>
              <button disabled={busy} onClick={() => decide('approved')} title={recommendOnly ? 'Over Rs 20,000 Rajan approves; yours is recorded as a recommendation and it stays with him' : undefined}
                style={{ padding: '9px 22px', background: recommendOnly ? '#7A5A00' : '#166534', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>
                {recommendOnly ? '👍 Recommend' : r.current_level === 'final_approver' ? '✅ Approve (final)' : '✅ Approve'}</button>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
